// ABOUTME: Provides localStorage persistence utilities for task/trainer sessions.
// ABOUTME: Session lifecycle (clear on navigation, preserve on reload) is managed by TaskSessionWatcher.

import { useRef, useCallback, useEffect } from 'react';

const PREFIX = 'task_session_';
const NAV_PREFIX = 'task_nav_';

/** Remove ALL task session and task nav keys from localStorage. */
export function clearAllTaskSessions() {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(PREFIX) || k.startsWith(NAV_PREFIX)) {
        toRemove.push(k);
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch {}
}

function getKey(pageType: string, taskId: string | undefined): string {
  return taskId ? `${PREFIX}${pageType}_${taskId}` : `${PREFIX}${pageType}`;
}

/**
 * Hook for persisting task session state in localStorage.
 * Reads saved state on mount. TaskSessionWatcher ensures stale sessions
 * are cleared on SPA navigation, so only reload data survives.
 * Provides save() and clear() helpers.
 */
export function useTaskSession<T extends Record<string, unknown> = Record<string, unknown>>(
  pageType: string,
  taskId: string | undefined,
) {
  const key = getKey(pageType, taskId);

  // Load once on first render
  const initial = useRef<Partial<T> | null>(null);
  if (initial.current === null) {
    try {
      const raw = localStorage.getItem(key);
      initial.current = raw ? JSON.parse(raw) : {};
    } catch {
      initial.current = {};
    }
  }

  const save = useCallback(
    (data: Record<string, unknown>) => {
      try {
        localStorage.setItem(key, JSON.stringify(data));
      } catch {}
    },
    [key],
  );

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } catch {}
  }, [key]);

  return { initial: initial.current as Partial<T>, save, clear };
}

/**
 * Auto-saves state to a task session whenever deps change.
 * Pairs with useTaskSession for loading.
 */
export function useAutoSaveSession(
  session: { save: (data: Record<string, unknown>) => void },
  getData: () => Record<string, unknown>,
  deps: unknown[],
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { session.save(getData()); }, deps);
}
